import CloseIcon from '@mui/icons-material/Close';
import OpenInNewOutlinedIcon from '@mui/icons-material/OpenInNewOutlined';
import Box from '@mui/material/Box';
import Dialog from '@mui/material/Dialog';
import DialogContent from '@mui/material/DialogContent';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { type ReactNode, useState } from 'react';
import { MediaCard } from '../../../media/shared/MediaCard.js';
import { SECTION_CAPTION_SX, SECTION_TITLE_SX } from './SectionHeader.js';

const ZOOM_DIALOG_SX = { width: 920, maxWidth: '90vw' };

interface ChartSectionProps {
  title: string;
  subtitle?: string;
  headerStart?: ReactNode;
  actions?: ReactNode;
  variant?: 'section' | 'card';
  children: ReactNode;
}

export function ChartSection({
  title,
  subtitle,
  headerStart,
  actions,
  variant = 'section',
  children,
}: ChartSectionProps): ReactNode {
  const [zoomOpen, setZoomOpen] = useState(false);

  const zoomButton = (
    <IconButton
      size="small"
      aria-label="Open full size"
      onClick={() => setZoomOpen(true)}
      sx={(theme) => ({
        width: 28,
        height: 28,
        border: '1px solid',
        borderColor: theme.palette.border.strong,
        color: 'text.secondary',
        borderRadius: `${theme.radius.md}px`,
      })}
    >
      <OpenInNewOutlinedIcon sx={{ fontSize: 15 }} />
    </IconButton>
  );

  const renderHeader = (showZoomButton: boolean): ReactNode => (
    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 3 }}>
        <Box>
          <Typography sx={SECTION_TITLE_SX}>{title}</Typography>
          {subtitle === undefined ? null : (
            <Typography sx={{ ...SECTION_CAPTION_SX, mt: 0.5 }}>{subtitle}</Typography>
          )}
        </Box>
        {headerStart}
      </Box>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
        {actions}
        {showZoomButton ? zoomButton : null}
      </Box>
    </Box>
  );

  const header = renderHeader(true);

  return (
    <Box>
      {variant === 'card' ? (
        <MediaCard sx={{ p: 4 }}>
          {header}
          <Box sx={{ mt: 3 }}>{children}</Box>
        </MediaCard>
      ) : (
        <>
          <Box sx={{ mb: 2 }}>{header}</Box>
          <MediaCard sx={{ p: 5 }}>{children}</MediaCard>
        </>
      )}
      <Dialog
        open={zoomOpen}
        onClose={() => setZoomOpen(false)}
        maxWidth={false}
        slotProps={{ paper: { sx: ZOOM_DIALOG_SX } }}
      >
        <DialogContent sx={{ p: 4 }}>
          <IconButton
            size="small"
            onClick={() => setZoomOpen(false)}
            aria-label="Close"
            sx={{ position: 'absolute', top: 12, right: 12, color: 'text.secondary' }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
          <Box sx={{ mb: 3, pr: 6 }}>{renderHeader(false)}</Box>
          {children}
        </DialogContent>
      </Dialog>
    </Box>
  );
}
